/**
 * Firestore の getDocs / getDoc をラップし、
 * 層2（クォータ予算ガード）と層3（サーキットブレーカー）を自動適用するユーティリティ。
 *
 * 使い方:
 *   import { guardedGetDocs, guardedGetDoc } from '../utils/guardedFirestore';
 *
 *   const snap = await guardedGetDocs(query);
 *   if (!snap) { /* クォータ超過 or サーキット遮断 *\/ return; }
 */

import {
  getDocs,
  getDoc,
  Query,
  DocumentReference,
  QuerySnapshot,
  DocumentSnapshot,
  DocumentData,
} from 'firebase/firestore';
import { quotaGuard } from './firestoreQuotaGuard';
import { circuitBreaker } from './circuitBreaker';

/**
 * getDocs のガード付きラッパー。
 * クォータ超過またはサーキット遮断中は null を返す（例外は投げない）。
 * Firestore エラーが発生した場合は例外を再スローする。
 */
export async function guardedGetDocs<T = DocumentData>(
  q: Query<T>
): Promise<QuerySnapshot<T> | null> {
  if (!quotaGuard.canRead()) {
    console.warn('[guardedGetDocs] Daily budget exceeded — skipping read');
    return null;
  }
  if (circuitBreaker.isOpen()) {
    console.warn('[guardedGetDocs] Circuit OPEN — skipping read');
    return null;
  }
  try {
    const snap = await getDocs(q);
    // 空クエリでも 1 read 消費するため最低 1 とカウント
    quotaGuard.recordReads(Math.max(snap.size, 1));
    circuitBreaker.recordSuccess();
    return snap;
  } catch (error) {
    circuitBreaker.recordFailure(error);
    throw error;
  }
}

/**
 * getDoc のガード付きラッパー。
 * クォータ超過またはサーキット遮断中は null を返す（例外は投げない）。
 */
export async function guardedGetDoc<T = DocumentData>(
  ref: DocumentReference<T>
): Promise<DocumentSnapshot<T> | null> {
  if (!quotaGuard.canRead()) {
    console.warn('[guardedGetDoc] Daily budget exceeded — skipping read');
    return null;
  }
  if (circuitBreaker.isOpen()) {
    console.warn('[guardedGetDoc] Circuit OPEN — skipping read');
    return null;
  }
  try {
    const snap = await getDoc(ref);
    quotaGuard.recordReads(1);
    circuitBreaker.recordSuccess();
    return snap;
  } catch (error) {
    circuitBreaker.recordFailure(error);
    throw error;
  }
}
